import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronUp, ArrowRight, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqCategories = [
    {
      category: "Export Controls & Licensing",
      questions: [
        {
          question: "How do I know if my product needs an export license?",
          answer: "Determining if your product requires an export license involves several steps: First, classify your item with an ECCN (Export Control Classification Number) or determine if it's subject to ITAR. Second, identify your destination country and end-user. Third, check if your specific combination of item, destination, and end-use requires a license or qualifies for a license exception. Our classification services can help you navigate this process accurately."
        },
        {
          question: "What's the difference between ITAR and EAR?",
          answer: "ITAR (International Traffic in Arms Regulations) controls defense articles and services listed on the U.S. Munitions List (USML), administered by the State Department. EAR (Export Administration Regulations) controls dual-use items and commercial products listed on the Commerce Control List (CCL), administered by the Bureau of Industry and Security. ITAR is generally more restrictive with stricter requirements for registration, licensing, and compliance."
        },
        {
          question: "How long does it take to get an export license?",
          answer: "Processing times vary by agency and license type. Commerce Department (BIS) licenses typically take 4-8 weeks, though they aim for decisions within 9 days for many applications. State Department (DDTC) licenses can take 60-90 days or longer for complex items. Expedited processing may be available in certain circumstances. Proper preparation and complete applications significantly reduce processing time."
        },
        {
          question: "Can I export technology or technical data?",
          answer: "Yes, but technology and technical data are subject to the same export controls as physical items. This includes electronic transfers, cloud storage, emails, and verbal disclosures to foreign nationals (deemed exports). You must classify your technology, determine if it requires authorization, and implement proper controls. Many companies underestimate technology transfer requirements, creating significant compliance risks."
        },
        {
          question: "What is a deemed export?",
          answer: "A deemed export occurs when controlled technology or technical data is released to a foreign national within the United States. This could happen during employment, facility tours, trade shows, or collaborative research. Deemed exports require the same authorization as physical exports to that person's country of nationality. This is a commonly overlooked compliance area that has resulted in substantial penalties."
        }
      ]
    },
    {
      category: "Product Classification",
      questions: [
        {
          question: "What is an ECCN and why do I need one?",
          answer: "An Export Control Classification Number (ECCN) is a five-character alphanumeric designation (e.g., 3A001) that identifies items on the Commerce Control List. You need an ECCN to determine export licensing requirements, qualify for license exceptions, and complete required export documentation. Accurate classification is fundamental to export compliance and reduces the risk of violations and penalties."
        },
        {
          question: "How do I classify my product for customs purposes?",
          answer: "Customs classification uses the Harmonized Tariff Schedule (HTS) for imports and Schedule B for exports. Classification is based on the product's material composition, function, and form. Proper classification determines applicable duty rates, trade agreements benefits, and import/export reporting requirements. We recommend obtaining binding rulings for complex products to ensure consistency and defensibility."
        },
        {
          question: "What if my product could fall under both ITAR and EAR?",
          answer: "When jurisdiction is unclear, you should request a Commodity Jurisdiction (CJ) determination from the State Department. This formal process provides an official determination of whether your item is controlled under ITAR or EAR. While the CJ process can take several months, it provides legal certainty and protection. Operating without clarity on jurisdiction creates significant compliance risk."
        }
      ]
    },
    {
      category: "Compliance Programs",
      questions: [
        {
          question: "Do I need a formal trade compliance program?",
          answer: "While not legally required for all companies, a documented compliance program is strongly recommended if you engage in international trade, especially with controlled items. Regulatory agencies expect reasonable compliance efforts appropriate to your business risk. A formal program demonstrates due diligence, helps prevent violations, and significantly reduces penalties if violations occur. Companies with effective compliance programs receive more favorable treatment from enforcement agencies."
        },
        {
          question: "What are the key elements of an export compliance program?",
          answer: "An effective compliance program includes: management commitment and adequate resources, written policies and procedures, screening processes for parties and transactions, product classification system, recordkeeping procedures, training programs for relevant personnel, internal audits and assessments, and response procedures for potential violations. The specific elements should be tailored to your company's risk profile, products, and markets."
        },
        {
          question: "How often should we conduct compliance training?",
          answer: "Initial training should occur during employee onboarding for anyone involved in international trade activities. Annual refresher training is recommended for most personnel, with more frequent updates when regulations change significantly. Role-specific training should be provided for key compliance personnel. Training effectiveness should be measured through assessments and incorporated into performance evaluations."
        }
      ]
    },
    {
      category: "Voluntary Disclosures",
      questions: [
        {
          question: "What should I do if I discover a potential violation?",
          answer: "First, stop the violative conduct immediately if still ongoing. Second, conduct an internal review to understand the scope and cause. Third, consult with experienced trade compliance counsel. Fourth, consider whether a voluntary self-disclosure (VSD) to the appropriate agency is warranted. Fifth, implement corrective measures to prevent recurrence. Prompt action and cooperation with authorities typically results in significantly reduced penalties."
        },
        {
          question: "What are the benefits of a voluntary self-disclosure?",
          answer: "VSDs can result in substantially reduced penalties, often 50% or more off the base penalty amount. They demonstrate good faith and compliance culture to regulators. They allow you to control the narrative and present mitigating factors. They show that your compliance program is functioning as intended by detecting issues. However, VSDs must be made before the government discovers the violation on its own to receive full credit."
        }
      ]
    },
    {
      category: "Foreign Trade Zones",
      questions: [
        {
          question: "What is a Foreign Trade Zone and how can it benefit my business?",
          answer: "Foreign Trade Zones (FTZs) are secured areas in the United States considered outside U.S. customs territory for duty purposes. Benefits include: duty deferral until goods enter U.S. commerce, duty elimination on re-exported goods, duty reduction through inverted tariff relief, no duties on waste/scrap, streamlined customs procedures, and potential state/local tax benefits. FTZs are particularly valuable for manufacturers, distributors, and companies with significant import/export activity."
        },
        {
          question: "How do I determine if an FTZ makes economic sense for my operation?",
          answer: "An FTZ cost-benefit analysis should consider: annual duty savings, inventory carrying cost reductions, weekly entry consolidation savings, state/local tax benefits, operational modifications required, FTZ activation and ongoing compliance costs, and staffing requirements. Most companies see positive ROI with annual import values exceeding $5-10 million, though specific circumstances vary. We can conduct a comprehensive feasibility analysis for your operation."
        }
      ]
    },
    {
      category: "Documentation & Procedures",
      questions: [
        {
          question: "What export documentation do I need to maintain?",
          answer: "Export documentation requirements depend on your products and destinations, but typically include: export licenses and authorizations, shipping documents (commercial invoices, packing lists, bills of lading), Electronic Export Information (EEI) filings, end-user certificates, import permits from destination countries, and compliance records (screening results, classification determinations). Records must be maintained for 5 years under EAR and ITAR, longer for customs purposes."
        },
        {
          question: "How long must I keep trade compliance records?",
          answer: "EAR and ITAR require 5 years from the date of export or other transaction. Customs regulations require 5 years from entry or export date. Some specialized scenarios require longer retention. We recommend 7 years for general trade compliance records to cover overlapping requirements and provide margin for uncertainty. Electronic recordkeeping is permitted but must ensure authenticity and accessibility."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="faq-page">
      <SEO 
        title="Trade Compliance FAQ | Export Controls & Licensing Questions"
        description="Frequently asked questions about export controls, ITAR, EAR, product classification, licensing, compliance programs, and international trade regulations. Expert answers to your compliance questions."
        keywords="export control FAQ, ITAR questions, trade compliance answers, export licensing help, product classification questions, customs compliance FAQ"
        path="/faq"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'FAQ', path: '/faq' }
      ]} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-8">
              <HelpCircle className="h-8 w-8 text-slate-700" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Common questions about export controls, product classification, compliance programs, and international trade regulations. Can't find what you're looking for? Contact us directly.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8 lg:px-12">
          <div className="space-y-12">
            {faqCategories.map((category, catIndex) => (
              <div key={catIndex}>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-slate-200">
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.questions.map((item, qIndex) => {
                    const isOpen = openIndex === `${catIndex}-${qIndex}`;
                    return (
                      <div 
                        key={qIndex}
                        className="border border-gray-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors"
                      >
                        <button
                          onClick={() => toggleQuestion(catIndex, qIndex)}
                          className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-lg font-semibold text-gray-900 flex-1">
                            {item.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="h-6 w-6 text-slate-700 flex-shrink-0 mt-0.5" />
                          ) : (
                            <ChevronDown className="h-6 w-6 text-slate-700 flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-6 py-5 bg-white">
                            <p className="text-base text-gray-700 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Still Have Questions */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <div className="bg-slate-50 rounded-2xl p-8 md:p-12 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Still have questions?
              </h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Every business has unique compliance challenges. Schedule a consultation with our experts to discuss your specific situation and requirements.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-8 h-12">
                    Schedule Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-slate-700 text-slate-700 hover:bg-slate-50 px-8 h-12">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Related Resources */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Link to="/glossary" className="group bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all">
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-slate-700">Trade Glossary</h4>
              <p className="text-sm text-gray-600 mb-3">Definitions of key trade compliance terms and acronyms</p>
              <span className="text-slate-700 font-semibold text-sm inline-flex items-center">
                Browse Terms <ArrowRight className="ml-1 h-4 w-4" />
              </span>
            </Link>
            <Link to="/resources" className="group bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all">
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-slate-700">Resource Library</h4>
              <p className="text-sm text-gray-600 mb-3">120+ links to government agencies and regulatory resources</p>
              <span className="text-slate-700 font-semibold text-sm inline-flex items-center">
                View Resources <ArrowRight className="ml-1 h-4 w-4" />
              </span>
            </Link>
            <Link to="/services" className="group bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all">
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-slate-700">Our Services</h4>
              <p className="text-sm text-gray-600 mb-3">Comprehensive trade compliance solutions and consulting</p>
              <span className="text-slate-700 font-semibold text-sm inline-flex items-center">
                Learn More <ArrowRight className="ml-1 h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
